import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  description: z.string().max(200, 'Description too long').optional(),
  applicationId: z.number().min(1, 'Application is required'),
})

export type TOrgTeamForm = z.infer<typeof formSchema>

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']
type MessagesSubscriptionsNewOrganisationTeam = RestSchemas['Messages.Subscriptions.NewOrganisationTeam']

type OrgTeamFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedTeam?: MessagesSubscriptionsOrganisationTeam
  delete?: boolean
}

export const OrgTeamForm = (props: OrgTeamFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  const { onSubmit: onSubmitProp, ...rest } = props
  const onSubmit = useCallback(() => {
    onSubmitProp?.()
    setSheetOpen(false)
  }, [onSubmitProp])

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...rest} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...rest} onSubmit={onSubmit} />
  )
}

const FormComponent = (props: OrgTeamFormProps) => {
  const edit = !!props.selectedTeam
  const del = edit && props.delete
  const { selectedTeam, onSubmit } = props

  const { id: orgId, name: orgName } = useCurrentOrganisation()

  const {
    getApplicationsApiGetApplications,
    getSubscriptionsApiGetOrganisationTeams,
    getSubscriptionsApiGetOrganisations,
    postSubscriptionsApiUpdateTeam,
    putSubscriptionsApiAddTeam,
    deleteSubscriptionsApiDeleteTeam,
  } = useRestClient()

  // Fetch applications for dropdown
  const {
    data: applications,
    isPending: isAppsPending,
    isError: isAppsError,
  } = getApplicationsApiGetApplications.useQuery<MessagesApplicationsApplication[]>()

  const { mutate: addTeam, isPending: isAddingTeam } = putSubscriptionsApiAddTeam.useMutation()
  const { mutate: updateTeam, isPending: isUpdatingTeam } = postSubscriptionsApiUpdateTeam.useMutation()
  const { mutate: deleteTeam, isPending: isDeletingTeam } = deleteSubscriptionsApiDeleteTeam.useMutation()

  const invalidateQueries = useCallback(() => {
    getSubscriptionsApiGetOrganisationTeams.invalidateQueries({
      parameters: { query: { orgId } },
    })
    getSubscriptionsApiGetOrganisations.invalidateQueries({})
  }, [orgId, getSubscriptionsApiGetOrganisationTeams, getSubscriptionsApiGetOrganisations])

  const defaultValues = useMemo(() => {
    if (edit && selectedTeam) {
      return {
        name: selectedTeam.name || '',
        description: selectedTeam.description || '',
        applicationId: selectedTeam.applicationId || 0,
      }
    }
    return {
      name: '',
      description: '',
      applicationId: 0,
    }
  }, [edit, selectedTeam])

  const form = useForm<TOrgTeamForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const onMutationSuccess = useCallback(() => {
    toast.success(del ? 'Team deleted' : edit ? 'Team successfully updated' : 'Team successfully added')
    onSubmit?.()
    form.reset()
    invalidateQueries()
  }, [del, edit, onSubmit, form, invalidateQueries])

  const handleSubmit = useCallback(
    async (data: TOrgTeamForm) => {
      if (!orgId) {
        toast.error('No organisation ID found')
        return
      }

      if (del) {
        const teamId = props.selectedTeam?.id

        deleteTeam(
          { query: { teamId } },
          {
            onSuccess: onMutationSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to delete team')
            },
          }
        )
      } else if (edit && selectedTeam) {
        const updatedTeam: MessagesSubscriptionsOrganisationTeam = {
          ...selectedTeam,
          name: data.name,
          description: data.description || '',
          applicationId: data.applicationId,
        }

        updateTeam(
          { body: updatedTeam },
          {
            onSuccess: onMutationSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to update team')
            },
          }
        )
      } else {
        const selectedApplication = applications?.find((app) => app.applicationId === data.applicationId)

        const newTeam: MessagesSubscriptionsNewOrganisationTeam = {
          id: 0,
          name: data.name,
          description: data.description || '',
          applicationId: data.applicationId,
          application: selectedApplication?.appName || '',
          organisationId: orgId,
          organisation: orgName || '',
          typeName: undefined,
          linkedProjectCount: 0,
          activeLinkedProjectCount: 0,
          linkedUserCount: 0,
          linkedRoleCount: 0,
        }

        addTeam(
          { body: newTeam },
          {
            onSuccess: onMutationSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to add team')
            },
          }
        )
      }
    },
    [
      orgId,
      del,
      edit,
      selectedTeam,
      props.selectedTeam?.id,
      deleteTeam,
      onMutationSuccess,
      updateTeam,
      applications,
      orgName,
      addTeam,
    ]
  )

  // Create options for the application combobox
  const applicationOptions = useMemo(() => {
    return (
      applications?.map((app) => ({
        value: app.applicationId || 0,
        label: app.appName || '',
        description: app.description,
      })) || []
    )
  }, [applications])

  const loading = isUpdatingTeam || isAddingTeam || isAppsPending || isDeletingTeam
  const error = isAppsError

  if (loading) return <FormComponentLoading />
  if (error) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                disabled={del}
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter team name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={del}
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter team description (optional)" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {edit && selectedTeam ? (
                <FormItem>
                  <FormLabel>Application</FormLabel>
                  <FormControl>
                    <Input value={selectedTeam.application || 'Application'} readOnly disabled={del} />
                  </FormControl>
                </FormItem>
              ) : (
                <SingleSelectComboboxField
                  control={form.control}
                  name="applicationId"
                  label="Application"
                  options={applicationOptions}
                  placeholder="Select an application..."
                  searchPlaceholder="Search applications..."
                  emptyMessage="No applications found."
                  allowClear={false}
                />
              )}
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={loading}>
                {del ? 'Delete Team' : edit ? 'Update Team' : 'Add Team'}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
